import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight, Calendar, Users, Heart, MapPin, Clock, Sparkles, ChevronRight, Mail, User, Phone, Loader2, Handshake, CalendarDays, Building2 } from 'lucide-react';
import { associationInfo, mission, upcomingEvents, stats, testimonials } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  return (
    <div ref={containerRef} className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-amber-50">
      {/* Navigation */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Logo />
          </motion.div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#about" className="text-slate-700 hover:text-orange-600 transition-colors">À propos</a>
            <a href="#events" className="text-slate-700 hover:text-orange-600 transition-colors">Activités</a>
            <a href="#membership" className="text-slate-700 hover:text-orange-600 transition-colors">Adhésion</a>
            <a href="/contact" className="text-slate-700 hover:text-orange-600 transition-colors">Contact</a>
          </div>
          <Button className="bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:shadow-lg hover:scale-105 transition-all">
            <a href="#membership">Rejoindre</a>
          </Button>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <motion.section
        style={{ opacity: heroOpacity, scale: heroScale }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-orange-200/30 to-amber-200/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [90, 0, 90],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-orange-300/20 to-amber-300/20 rounded-full blur-3xl"
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-orange-200 mb-8"
          >
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-slate-700">Une communauté de plus de 3 000 personnes</span>
          </motion.div>

          <motion.h1
            className="text-6xl md:text-8xl font-bold mb-6 leading-tight"
          >
            {associationInfo.tagline.split(' ').map((word, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + i * 0.1 }}
                className="inline-block mr-4 bg-gradient-to-br from-slate-900 via-orange-800 to-amber-900 bg-clip-text text-transparent"
              >
                {word}
              </motion.span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-xl md:text-2xl text-slate-600 mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            {associationInfo.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button size="lg" className="bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:shadow-2xl hover:scale-105 transition-all text-lg px-8 py-6">
              <a href="#membership" className="flex items-center gap-2">
                Devenir membre
                <ArrowRight className="w-5 h-5" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 text-lg px-8 py-6">
              <a href="#events" className="flex items-center gap-2">
                Découvrir nos activités
                <ChevronRight className="w-5 h-5" />
              </a>
            </Button>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-orange-400 rounded-full flex items-start justify-center p-2"
          >
            <motion.div className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Stats Section */}
      <StatsSection stats={stats} />

      {/* Mission Section */}
      <MissionSection mission={mission} />

      {/* Events Section */}
      <EventsSection events={upcomingEvents} />

      {/* Nous organisons aussi des événements */}
      <EventsProSection />

      {/* Partenariats */}
      <PartnershipsSection />

      {/* Membership CTA */}
      <MembershipSection />

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Stats Section Component
const StatsSection = ({ stats }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center"
            >
              <div className="text-5xl md:text-6xl font-bold bg-gradient-to-br from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2">
                {stat.value}
              </div>
              <div className="text-slate-600 font-medium text-sm md:text-base">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Mission Section Component
const MissionSection = ({ mission }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section id="about" ref={ref} className="py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-slate-900 to-orange-800 bg-clip-text text-transparent">
            Notre Mission
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Quatre piliers pour construire ensemble un patrimoine culturel vivant
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {mission.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              <Card className="group h-full border-2 border-slate-200 hover:border-orange-400 hover:shadow-2xl transition-all duration-500 overflow-hidden">
                <CardContent className="p-8">
                  <div className="text-8xl font-bold text-orange-100 group-hover:text-orange-200 transition-colors mb-4">
                    {item.number}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-slate-900">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Events Section Component
const EventsSection = ({ events }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <section id="events" ref={ref} className="py-32 px-6 bg-gradient-to-br from-orange-50 to-amber-50">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-slate-900 to-orange-800 bg-clip-text text-transparent">
            Exemples d'activités proposées
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-4">
            Un aperçu des ateliers, sorties et rencontres que nous proposons à notre communauté
          </p>
          <p className="text-sm text-slate-500 max-w-3xl mx-auto italic">
            * Les activités sont soumises à l'adhésion approuvée à l'association et au règlement (paiement) complet de l'activité
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {events.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="group overflow-hidden border-2 border-slate-200 hover:border-orange-400 hover:shadow-2xl transition-all duration-500 h-full">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/90 backdrop-blur-sm text-orange-600 border-orange-200">
                      {event.category}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3 text-slate-900 group-hover:text-orange-600 transition-colors">
                    {event.title}
                  </h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">{event.description}</p>
                  <div className="space-y-2 text-sm text-slate-500">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-orange-600" />
                      <span className="font-medium text-orange-600">Dates disponibles dans l'espace adhérent</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-orange-600" />
                      <span>{event.location}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <p className="text-xs text-slate-400 italic">* Adhésion et règlement requis</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Nous organisons aussi des événements
const EventsProSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <section ref={ref} className="py-32 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full border border-orange-200 mb-6">
            <CalendarDays className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-700">Organisation d'événements</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-br from-slate-900 to-orange-800 bg-clip-text text-transparent">
            Nous organisons aussi des événements
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-lg text-slate-700 mb-6 leading-relaxed">
              APELGP organise et coordonne différents formats d'événements pour favoriser 
              les rencontres, la convivialité et la découverte : ateliers culturels et 
              culinaires, sorties, rencontres culturelles, événements thématiques, activités 
              de groupe, séjours et temps forts organisés avec nos partenaires.
            </p>
            <p className="text-lg text-slate-700 mb-8 leading-relaxed">
              Nous collaborons avec des lieux, prestataires, institutions et acteurs du 
              tourisme, de la culture, du patrimoine, de la gastronomie, des loisirs et de 
              l'événementiel pour construire de nouvelles opérations adaptées à notre communauté.
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:shadow-xl hover:scale-105 transition-all"
              data-testid="propose-event-btn"
            >
              <a href="/contact" className="flex items-center gap-2">
                Proposer un événement ou un partenariat
                <ArrowRight className="w-5 h-5" />
              </a>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="grid grid-cols-2 gap-4"
          >
            {[
              { icon: CalendarDays, label: "Événements thématiques" },
              { icon: Users, label: "Activités de groupe" },
              { icon: MapPin, label: "Sorties & séjours" },
              { icon: Sparkles, label: "Temps forts" }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border-2 border-orange-100 hover:border-orange-300 transition-all">
                <item.icon className="w-8 h-8 text-orange-600 mb-3" />
                <p className="text-slate-700 font-medium text-sm">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Partnerships Section
const PartnershipsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const partners = [
    { icon: Building2, label: "Tourisme & Hôtellerie" },
    { icon: Sparkles, label: "Culture & Patrimoine" },
    { icon: Heart, label: "Gastronomie" },
    { icon: CalendarDays, label: "Loisirs" },
    { icon: Users, label: "Événementiel" }
  ];

  return (
    <section ref={ref} className="py-32 px-6 bg-gradient-to-br from-slate-50 to-orange-50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-orange-200 mb-6">
            <Handshake className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-700">Collaborations</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-br from-slate-900 to-orange-800 bg-clip-text text-transparent">
            Partenariats
          </h2>
          <p className="text-lg text-slate-700 max-w-3xl mx-auto leading-relaxed">
            APELGP développe des partenariats avec des acteurs du tourisme, de la culture, 
            du patrimoine, de la gastronomie, des loisirs et de l'événementiel afin de proposer 
            à sa communauté de nouvelles expériences, rencontres, sorties et séjours culturels.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex flex-wrap justify-center gap-6 mb-12"
        >
          {partners.map((partner, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 border-2 border-slate-200 hover:border-orange-400 hover:shadow-lg transition-all min-w-[140px] text-center">
              <partner.icon className="w-8 h-8 text-orange-600 mx-auto mb-3" />
              <p className="text-slate-700 font-medium text-sm">{partner.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center"
        >
          <Button
            size="lg"
            variant="outline"
            className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 hover:scale-105 transition-all"
            data-testid="propose-partnership-btn"
          >
            <a href="/contact" className="flex items-center gap-2">
              Nous proposer un partenariat
              <ArrowRight className="w-5 h-5" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = ({ testimonials }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section ref={ref} className="py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-slate-900 to-orange-800 bg-clip-text text-transparent">
            Témoignages
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Ce que nos membres disent de nous
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <Card className="h-full border-2 border-slate-200 hover:border-orange-400 hover:shadow-xl transition-all duration-500">
                <CardContent className="p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Heart key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                  <div>
                    <div className="font-bold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Membership Section
const MembershipSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    partnerCode: '',
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.partnerCode) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/membership/register`, formData);
      
      if (response.data.success) {
        setIsSuccess(true);
        toast.success(response.data.message);
        
        // Reset form
        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          phone: '',
          partnerCode: '',
          website: ''
        });
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || 
        "Une erreur est survenue. Veuillez réessayer."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="membership" ref={ref} className="py-32 px-6 bg-gradient-to-br from-orange-600 to-amber-600 text-white relative overflow-hidden">
      <div className="absolute inset-0">
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 0],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -top-1/2 -right-1/2 w-full h-full bg-white/5 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto">
        {!isSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="text-center mb-12">
              <Users className="w-20 h-20 mx-auto mb-6" />
              <h2 className="text-5xl md:text-6xl font-bold mb-6">
                Rejoignez Notre Communauté
              </h2>
              <p className="text-xl text-orange-50 max-w-2xl mx-auto leading-relaxed">
                Rejoignez notre communauté de passionnés de culture, de patrimoine, de gastronomie 
                et de loisirs. Participez à nos ateliers, sorties, rencontres, événements et 
                séjours culturels tout au long de l'année.
              </p>
            </div>

            <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-2xl max-w-2xl mx-auto">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" autoComplete="on">
                  {/* Honeypot field - hidden from users, visible to bots */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                    <label htmlFor="website-adh">Ne pas remplir ce champ</label>
                    <input
                      type="text"
                      id="website-adh"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={formData.website}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-slate-700 font-medium">
                        Prénom <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Jean"
                          className="pl-10 border-2 border-slate-200 focus:border-orange-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-slate-700 font-medium">
                        Nom <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Dupont"
                          className="pl-10 border-2 border-slate-200 focus:border-orange-500"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="jean.dupont@example.com"
                        className="pl-10 border-2 border-slate-200 focus:border-orange-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-medium">
                      Téléphone (optionnel)
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+33 6 12 34 56 78"
                        className="pl-10 border-2 border-slate-200 focus:border-orange-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="partnerCode" className="text-slate-700 font-medium">
                      Code de la structure partenaire <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="partnerCode"
                        name="partnerCode"
                        type="text"
                        value={formData.partnerCode}
                        onChange={handleChange}
                        placeholder="Votre code structure"
                        className="pl-10 border-2 border-slate-200 focus:border-orange-500"
                        required
                      />
                    </div>
                  </div>

                  <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 space-y-2">
                    <p className="text-sm text-slate-600">
                      En soumettant ce formulaire, vous recevrez un email de confirmation. 
                      Vous devez cliquer sur le lien dans l'email pour valider votre adhésion.
                    </p>
                    <p className="text-sm text-slate-700 font-medium">
                      ⚠️ L'adhésion est subordonnée à l'acceptation de l'association et du code de la structure partenaire adhérente à l'association.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white hover:shadow-xl text-lg py-6 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        S'inscrire maintenant
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-12 max-w-2xl mx-auto">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', duration: 0.6, delay: 0.2 }}
              >
                <Mail className="w-24 h-24 mx-auto mb-6 text-orange-600" />
              </motion.div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">
                Vérifiez votre email !
              </h3>
              <p className="text-lg text-slate-600 mb-6">
                Nous avons envoyé un email de confirmation à votre adresse. 
                Veuillez cliquer sur le lien dans l'email pour finaliser votre adhésion.
              </p>
              <p className="text-sm text-slate-500">
                Si vous ne recevez pas l'email dans quelques minutes, vérifiez votre dossier spam.
              </p>
              <Button
                onClick={() => setIsSuccess(false)}
                className="mt-8 bg-gradient-to-r from-orange-600 to-amber-600 text-white"
              >
                Nouvelle inscription
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer id="contact" className="bg-slate-900 text-white py-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <div className="inline-block bg-white/95 rounded-xl px-4 py-3 mb-4">
              <Logo />
            </div>
            <p className="text-slate-400 mb-6 leading-relaxed">
              {associationInfo.description}
            </p>
            <div className="text-sm text-slate-500">
              <p>SIRET: {associationInfo.siret}</p>
              <p>RNA: {associationInfo.rna}</p>
            </div>
          </div>
          <div>
            <h4 className="font-bold mb-4 text-orange-400">Navigation</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#about" className="hover:text-orange-400 transition-colors">À propos</a></li>
              <li><a href="#events" className="hover:text-orange-400 transition-colors">Activités</a></li>
              <li><a href="#membership" className="hover:text-orange-400 transition-colors">Adhésion</a></li>
              <li><a href="/contact" className="hover:text-orange-400 transition-colors">Contact</a></li>
              <li><a href="/mentions-legales" className="hover:text-orange-400 transition-colors">Mentions légales</a></li>
              <li><a href="/politique-confidentialite" className="hover:text-orange-400 transition-colors">Politique de confidentialité</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-bold mb-4 text-orange-400">Contact</h4>
            <ul className="space-y-2 text-slate-400">
              <li>{associationInfo.address}</li>
              <li>{associationInfo.city}</li>
              <li className="pt-2 text-xs text-slate-500">Association à but non lucratif</li>
              <li className="text-sm">
                <a href="/contact" className="text-orange-400 hover:text-orange-300 transition-colors" data-testid="footer-contact-link">Contactez-nous via le formulaire</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-8 text-center text-slate-500">
          <p>&copy; 2026 {associationInfo.name}. Tous droits réservés. | Association créée le {associationInfo.foundedDate}</p>
        </div>
      </div>
    </footer>
  );
};

export default Home;